import { Link, useParams } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';

import { ListTypeEnum } from 'APIPackage/CatalogsModule/enums/ListTypeEnum';
import {
  getIsPlanErknmStatusInProcess,
  getIsPlanErknmStatusApproved,
} from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import { Button } from 'UIPackage/MainModule/components/Button';
import { ROUTE_KNM_LIST_BY_ID_AND_TYPE } from 'CorePackage/MainModule/routes/routes';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { getCatalogListTypes } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IListType } from 'APIPackage/CatalogsModule/interfaces/IListType';
import { IKnmPlanInfo } from 'APIPackage/PlanListModule/interfaces/IKnmPlanInfo';
import {
  getPlanData,
  getPlanDataErknmStatus,
  getPlanDataKnoOrganizationDictVersionId,
  getPlanDataSupervisionDictVersionId,
  getPlanInfo,
  getPlanPositionsSelectOptions,
  getPlanSupervisionTypeValue,
  getPlanWatchOnlyByTestUser,
} from 'PlanPackage/MainModule/selectors/planSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IPosition } from 'APIPackage/DictionariesModule/interfaces/IPosition';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import {
  dictionariesItemValueGet,
  dictionariesKnoSupervisionGet,
} from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { FormErrors } from 'KnmPackage/MainModule/types/FormErrors';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { getDictionarySelectOptionWithState } from 'CorePackage/DictionariesModule/common/getDictionarySelectOptionWithState';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { store } from 'CorePackage/MainModule/common/store';

import { filterListTypesByPlanStatus } from '../common/filterListTypesByPlanStatus';
import { KnmListTypesWithParamsEnum } from '../enums/KnmListTypesWithParamsEnum';
import { ListTypesTextEnum } from '../enums/ListTypesTextEnum';
import { ISupervisionType } from '../../../APIPackage/DictionariesModule/interfaces/ISupervisionType';
import { IPlanForm } from '../interfaces/PlanForm';
import styles from './PlanBody.module.scss';
import { planSetFormRef } from '../actions/planActions';

interface IPlanBody {
  handleAddKnmButtonClick: () => void;
}

interface IPlanBodyStoreProps {
  isSupervisor: boolean;
  plan: IKnmPlan | undefined;
  planErknmStatus: ErknmPlanStatusesEnum | undefined;
  positionsSelectOptions: IDictionarySelectOption<IPosition>[] | undefined;
  planInfo: IKnmPlanInfo | undefined;
  listTypes: IListType[];
  supervisionTypeValue?: ISupervisionType;
  isWatchOnlyByTestUser: boolean;
  settings: IAdminSetting[] | undefined;
  knoOrganizationDictVersionId: string | undefined;
  supervisionTypeDictVersionId: string | undefined;
}

const PlanBodyWithoutStore = ({
  handleAddKnmButtonClick,
  isSupervisor,
  listTypes,
  plan,
  planErknmStatus,
  planInfo,
  supervisionTypeValue,
  isWatchOnlyByTestUser,
  settings,
  positionsSelectOptions,
  knoOrganizationDictVersionId,
  supervisionTypeDictVersionId,
}: IPlanBody & IPlanBodyStoreProps) => {
  const isPlanErknmStatusInProcess: boolean = getIsPlanErknmStatusInProcess(planErknmStatus);
  const isPlanErknmStatusApproved: boolean = getIsPlanErknmStatusApproved(planErknmStatus);
  const filteredListTypes = filterListTypesByPlanStatus(listTypes, planErknmStatus);
  const [shouldStartLoadingSignerPosition, setShouldStartLoadingSignerPosition] = useState(false);
  const [isFoundSignerPosition, setIsFoundSignerPosition] = useState(false);

  const dispatch = useDispatch<Dispatch<RootActions>>();

  const { id: planId } = useParams<{ id: string }>();

  const createValueOption = (value: string | undefined) => (value ? { id: 1, label: value, value } : undefined);

  useEffect(() => {
    if (!shouldStartLoadingSignerPosition && plan?.knmPlanData?.signerPosition) {
      dispatch(
        dictionariesItemValueGet(plan.knmPlanData.signerPosition.dictId, plan.knmPlanData.signerPosition.dictVersionId),
      );
      setShouldStartLoadingSignerPosition(true);
    }
  }, [plan?.knmPlanData?.signerPosition]);

  useEffect(() => {
    if (knoOrganizationDictVersionId && supervisionTypeDictVersionId) {
      dispatch(dictionariesKnoSupervisionGet(knoOrganizationDictVersionId, supervisionTypeDictVersionId));
    }
  }, [knoOrganizationDictVersionId, supervisionTypeDictVersionId]);

  const valueSupervisionTypeOption = useMemo(() => {
    if (supervisionTypeValue) {
      const value = `${supervisionTypeValue?.code} - ${supervisionTypeValue?.title}`;

      return { id: 1, label: value, value };
    }

    return null;
  }, [supervisionTypeValue]);

  const isFioAndJobTitle414 = isLaterThanAdminSetting(
    plan?.created,
    settings,
    AdminSettingCodesEnum.PLAN_SIGNER_166_4_1_4_START_FROM,
  );

  /**
   * Блокируем поля подписанта только после фактически выполненного подписания плана.
   * Наличие signedXlsxFile признаком подписания не является: бэк сохраняет файл
   * и подпись даже тогда, когда подписание отклонено валидациями (E21001/E21002/E21005),
   * поэтому дополнительно проверяем признак подписи и заполненность данных подписанта.
   */
  const isSignerFieldsDisabled = useMemo(() => {
    const isPlanSigned = Boolean(plan?.signed) || Boolean(plan?.knmPlanData?.signedXlsxFile?.signed);
    const isSignerDataFilled = Boolean(
      plan?.knmPlanData?.signerFullName && plan?.knmPlanData?.signerPosition?.dictVersionId,
    );

    return isPlanSigned && isSignerDataFilled;
  }, [plan]);

  const renderForm = (form: FormRenderProps<IPlanForm, Partial<IPlanForm>>) => {
    const setFormRef = useCallback(formRef => dispatch(planSetFormRef(formRef)), [dispatch]);

    const formRef = useRef(form);

    formRef.current = form;

    useEffect(() => {
      if (positionsSelectOptions && plan?.knmPlanData?.signerPosition && !isFoundSignerPosition) {
        const state = store.getState();
        const signerPositionValue: IDictionaryPositionSelectOption | undefined = getDictionarySelectOptionWithState<
          typeof DictionaryUUIDsEnum.PARTICIPANT_POSITIONS
        >(state, plan?.knmPlanData?.signerPosition.dictId, plan?.knmPlanData?.signerPosition.dictVersionId);

        setIsFoundSignerPosition(true);
        form.form.change('signerPosition', signerPositionValue);
      } else {
        setIsFoundSignerPosition(false);
      }
    }, [positionsSelectOptions, plan]);

    useEffect(() => {
      setFormRef(formRef);
    }, [setFormRef, formRef]);

    return (
      <>
        <form onSubmit={form.handleSubmit}>
          <div className={styles.FormContent}>
            <FieldBlock color="black">
              <div className="shared-group-margin">
                <label className="shared-group-label" htmlFor="kindControl">
                  ФИО ответственного лица, утверждающего план <span className={styles.RedMark}> *</span>
                </label>
              </div>
              <TextInputField
                id="signerFullName"
                name="signerFullName"
                disabled={isSignerFieldsDisabled}
                hadToBeHowInSignMessage={null}
              />
            </FieldBlock>
            <FieldBlock color="black">
              <div className="shared-group-margin">
                <label className="shared-group-label" htmlFor="kindControl">
                  Должность ответственного лица, утверждающего план <span className={styles.RedMark}> *</span>
                </label>
              </div>
              <SelectInputField
                id="signerPosition"
                name="signerPosition"
                options={positionsSelectOptions}
                disabled={isSignerFieldsDisabled}
                size="medium"
              />
            </FieldBlock>
          </div>
        </form>
      </>
    );
  };

  const validateForm = useCallback(
    (values: IPlanForm) => {
      const formErrors: FormErrors<IPlanForm> = {
        signerFullName: isFioAndJobTitle414
          ? makeRequired('Поле не может быть пустым')(values?.signerFullName)
          : undefined,
        signerPosition: isFioAndJobTitle414
          ? makeRequired('Поле не может быть пустым')(values?.signerPosition)
          : undefined,
      };

      return formErrors;
    },
    [isFioAndJobTitle414],
  );

  const handleSubmit = (data: IPlanForm) => {
    // eslint-disable-next-line no-console
    console.log('form data', data);
  };

  const form = (
    <Form<IPlanForm>
      onSubmit={handleSubmit}
      initialValues={{
        signerFullName: plan?.knmPlanData?.signerFullName || '',
        // signerPosition: '',
      }}
      validate={validateForm}
      render={renderForm}
    />
  );

  return (
    <div className={styles.PlanBody}>
      {isFioAndJobTitle414 && <div>{form}</div>}
      <div className={styles.PlanBodyMain}>
        <h2 className="shared-title__with-margin">Общая информация</h2>
        <div>
          <div className={styles.PlanBodyFieldBlock}>
            <div className="shared-group-margin">
              <label className="shared-group-label" htmlFor="knoOrganizationErknm">
                Контрольный (надзорный) орган
              </label>
            </div>
            <SelectInput name="knoName" disabled value={createValueOption(plan?.knoName)} />
          </div>
          <div className={styles.PlanBodyFieldBlock}>
            <div className="shared-group-margin">
              <label className="shared-group-label" htmlFor="kindControl">
                Орган прокуратуры
              </label>
            </div>
            <SelectInput name="prosecName" disabled value={createValueOption(plan?.prosecName)} />
          </div>
          {supervisionTypeValue && (
            <div className={styles.PlanBodyFieldBlock}>
              <div className="shared-group-margin">
                <label className="shared-group-label" htmlFor="supervisionTypeErknm">
                  Вид контроля (надзора)
                </label>
              </div>
              <SelectInput name="knoName" disabled value={valueSupervisionTypeOption} />
            </div>
          )}
        </div>
      </div>
      <div className={styles.PlanBodyComposition}>
        <div className={styles.PlanBodyHeaderContainer}>
          <h2 className={styles.Title}>Состав плана</h2>
          {!isWatchOnlyByTestUser && isSupervisor && (isPlanErknmStatusInProcess || isPlanErknmStatusApproved) && (
            <Button
              className={styles.PlanBodyAddButton}
              text="Добавить КНМ"
              color="primary"
              disabled={plan?.deleted}
              onClick={handleAddKnmButtonClick}
            />
          )}
        </div>
        <div className={styles.PlanBodyCompositionContainer}>
          {filteredListTypes.map(item => {
            if (planInfo) {
              const knmNumber = planInfo[KnmListTypesWithParamsEnum[item.type]];

              return (
                planInfo && (
                  <Link
                    to={ROUTE_KNM_LIST_BY_ID_AND_TYPE(planId, item.type.toLowerCase() as ListTypeEnum)}
                    className={styles.PlanBodyCompositionItem}
                    key={item.type}
                  >
                    <span
                      className={
                        knmNumber
                          ? (item.type === ListTypeEnum.AGREED && styles.PlanBodyCompositionNumberActiveStatus) ||
                            (item.type === ListTypeEnum.EXCLUDED && styles.PlanBodyCompositionNumberInactiveStatus) ||
                            styles.PlanBodyCompositionNumber
                          : styles.PlanBodyCompositionNumberZero
                      }
                    >
                      {knmNumber}
                    </span>
                    <span className={styles.PlanBodyCompositionName}>{ListTypesTextEnum[item.type]}</span>
                  </Link>
                )
              );
            }

            return false;
          })}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IPlanBodyStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
  plan: getPlanData(state),
  planErknmStatus: getPlanDataErknmStatus(state),
  positionsSelectOptions: getPlanPositionsSelectOptions(state),
  planInfo: getPlanInfo(state),
  supervisionTypeValue: getPlanSupervisionTypeValue(state),
  settings: getAdminFetchSettings(state),
  listTypes: getCatalogListTypes(state),
  isWatchOnlyByTestUser: getPlanWatchOnlyByTestUser(state),
  knoOrganizationDictVersionId: getPlanDataKnoOrganizationDictVersionId(state),
  supervisionTypeDictVersionId: getPlanDataSupervisionDictVersionId(state),
});

export const PlanBody = connect(mapStateToProps)(PlanBodyWithoutStore);
